// "Log something earlier": for the nap nobody tapped, the wee in the rush
// out of the door, the meal logged an hour late. Pick what, roughly when,
// (for naps) roughly how long — then the next one, without leaving the sheet.
// Opened from anywhere via openEarlier(); rendered once by <EarlierHost/>.

import { History, Undo2 } from 'lucide-react';
import { useMemo, useState, useSyncExternalStore } from 'react';
import { deleteEvent, endSleep, logEvent, openDuration, updateEvent } from '../../data/actions.ts';
import { useEvents, useLiveStatus } from '../../data/hooks.ts';
import { TIMED, clash, describeSpan, earlierEntry, lastedEnd, resolveWhen, type EarlierKind, type Lasted, type When } from '../../domain/earlier.ts';
import { DAY, MINUTE, dayStart, formatDuration, formatTime, toISODate } from '../../domain/time.ts';
import type { LogEvent } from '../../domain/types.ts';
import { Button, Field, Segmented, Sheet } from '../../ui/primitives.tsx';
import { toast } from '../../ui/toast.tsx';

let opened: { kind: EarlierKind; ongoing?: boolean; key: number } | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

/** Open the sheet on a kind; `ongoing` starts on "still asleep" (he went down before anyone tapped Nap). */
export function openEarlier(kind: EarlierKind = 'nap', opts: { ongoing?: boolean } = {}) {
  opened = { kind, ongoing: opts.ongoing, key: Date.now() };
  emit();
}

function close() {
  opened = null;
  emit();
}

export function EarlierHost() {
  const s = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => opened,
  );
  if (!s) return null;
  return <Earlier key={s.key} initial={s.kind} ongoing={s.ongoing} />;
}

const KINDS: { value: EarlierKind; label: string }[] = [
  { value: 'nap', label: 'Nap' },
  { value: 'wee', label: 'Wee' },
  { value: 'poo', label: 'Poo' },
  { value: 'accident', label: 'Accident' },
  { value: 'meal', label: 'Meal' },
  { value: 'play', label: 'Play' },
  { value: 'walk', label: 'Outing' },
  { value: 'training', label: 'Training' },
];

const AGO = [10, 20, 30, 45, 60, 90, 120, 180];
const LASTED = [20, 30, 45, 60, 90, 120];

const agoLabel = (m: number) => (m < 60 ? `${m}m ago` : `${m % 60 ? `${Math.floor(m / 60)}½` : m / 60}h ago`);
const lastedLabel = (m: number) => (m < 60 ? `${m} min` : m % 60 ? `${Math.floor(m / 60)}½ h` : `${m / 60} h`);

/** An exact time: type it, or nudge it five minutes either way. */
function TimeField({ label, value, onChange, max }: { label: string; value: number; onChange: (t: number) => void; max: number }) {
  const nudge = (d: number) => onChange(Math.min(max, value + d * MINUTE));
  return (
    <div className="earlier-time">
      <span className="field-label">{label}</span>
      <div className="earlier-time-row">
        <button type="button" className="chip" aria-label={`${label} 5 minutes earlier`} onClick={() => nudge(-5)}>
          −5
        </button>
        <input
          type="time"
          className="input"
          aria-label={label}
          value={formatTime(value)}
          onChange={(e) => {
            if (!e.target.value) return;
            const [h, m] = e.target.value.split(':').map(Number);
            const d = new Date(value);
            onChange(new Date(d.getFullYear(), d.getMonth(), d.getDate(), h, m).getTime());
          }}
        />
        <button type="button" className="chip" aria-label={`${label} 5 minutes later`} disabled={value + 5 * MINUTE > max} onClick={() => nudge(5)}>
          +5
        </button>
      </div>
    </div>
  );
}

function Earlier({ initial, ongoing }: { initial: EarlierKind; ongoing?: boolean }) {
  const s = useLiveStatus(30_000);
  const events = useEvents();
  const [kind, setKind] = useState<EarlierKind>(initial);
  const [when, setWhen] = useState<When>({ ago: ongoing ? 20 : initial === 'nap' ? 90 : 30 });
  const [lasted, setLasted] = useState<Lasted>(ongoing ? 'ongoing' : 60);
  const [accident, setAccident] = useState<'wee' | 'poo'>('wee');
  const [added, setAdded] = useState<{ id: string; label: string; undo: () => void }[]>([]);

  const now = s.now;
  const timed = TIMED.includes(kind);
  const at = resolveWhen(when, now);
  const day = toISODate(at);
  const todayISO = toISODate(now);
  const yesterdayISO = toISODate(dayStart(now, -1));
  const onToday = day === todayISO;
  /** Move to another day, keeping the time of day (today at a time still to come falls back to "30 min ago"). */
  const pickDay = (date: string) => {
    if (!date) return;
    const clock = formatTime(at);
    // Keep how long it lasted, not the clock time it ended (that would be on the old day).
    if (typeof lasted === 'object') setLasted(Math.round(((lastedEnd(lasted, at) ?? at) - at) / MINUTE));
    if (date === todayISO && resolveWhen({ clock, date }, now) > now) setWhen({ ago: 30 });
    else setWhen({ clock, date });
  };
  const open = kind === 'nap' ? openDuration('sleep') : kind === 'play' || kind === 'walk' ? openDuration(kind) : undefined;
  // A meal's grams: the planned meal nearest that time.
  const grams = kind === 'meal' ? [...s.meals.meals].sort((a, b) => Math.abs(a.at - at) - Math.abs(b.at - at))[0]?.grams : undefined;
  const entry = earlierEntry(kind, at, { lasted, accident, grams, now });
  // "Still asleep" when he's already logged asleep means "he went down earlier than we tapped".
  const moveOpen = timed && lasted === 'ongoing' && open ? open : undefined;
  const overlap = useMemo(() => (moveOpen ? undefined : clash(entry, events, now)), [moveOpen, entry, events, now]);
  // "Woke at 13:00" with a 13:30 start would mean tomorrow: say so rather than quietly cutting it to now.
  const endTooLate = timed && (lastedEnd(lasted, at) ?? 0) > now + MINUTE && typeof lasted === 'object';
  const future = at > now || endTooLate;
  const label = KINDS.find((k) => k.value === kind)!.label;
  const summary = `${kind === 'accident' ? `Accident (${accident})` : label} · ${describeSpan(entry, now)}${entry.end !== undefined ? ` (${formatDuration(entry.end - entry.at)})` : ''}`;

  const add = () => {
    if (moveOpen) {
      const before = moveOpen;
      updateEvent({ ...moveOpen, at } as LogEvent);
      setAdded((a) => [...a, { id: before.id + at, label: `${label} start moved to ${formatTime(at)}`, undo: () => updateEvent(before) }]);
    } else {
      const ev = logEvent(entry.type, entry.data, { at: entry.at, end: entry.end, note: 'logged later' });
      setAdded((a) => [...a, { id: ev.id, label: summary, undo: () => deleteEvent(ev.id) }]);
    }
    // Ready for the next thing: wees and poos usually come in pairs.
    if (kind === 'wee') setKind('poo');
  };

  const [wokeClock, setWokeClock] = useState('');
  const wokeAt = (mins: number) => {
    const ended = endSleep(now - mins * MINUTE);
    if (ended) {
      setAdded((a) => [...a, { id: `woke${ended.id}`, label: `Woke ${formatTime(ended.end!)}`, undo: () => updateEvent({ ...ended, end: undefined } as LogEvent) }]);
      toast({ message: `Nap ended at ${formatTime(ended.end!)}`, tone: 'good' });
    }
  };

  return (
    <Sheet
      open
      onClose={close}
      title="Log something earlier"
      footer={
        <>
          <span className="spacer" />
          <Button variant={added.length ? 'primary' : 'ghost'} onClick={close}>
            {added.length ? 'Done' : 'Cancel'}
          </Button>
          <Button variant={added.length ? 'soft' : 'primary'} icon={History} onClick={add} disabled={future || (!!overlap && !timed)}>
            {moveOpen ? 'Move start' : 'Add'}
          </Button>
        </>
      }
    >
      <div className="form earlier">
        <p className="muted small">Forgot to tap it at the time? Roughly is fine — it keeps nap times, toilet reminders and the charts right.</p>
        <Field label="What">
          <div className="conv-filter">
            {KINDS.map((k) => (
              <button key={k.value} type="button" className={`chip${kind === k.value ? ' on' : ''}`} aria-pressed={kind === k.value} onClick={() => setKind(k.value)}>
                {k.label}
              </button>
            ))}
          </div>
        </Field>
        {kind === 'nap' && s.sleep.asleep && s.sleep.current && (
          <p className="callout tone-info small">
            Logged asleep since {formatTime(s.sleep.current.at)}. Actually woke earlier?{' '}
            {[10, 20, 30, 60].map((m) =>
              now - m * MINUTE > s.sleep.current!.at ? (
                <button key={m} type="button" className="chip" onClick={() => wokeAt(m)}>
                  {agoLabel(m)}
                </button>
              ) : null,
            )}
            <span className="woke-at">
              or at{' '}
              <input type="time" className="input" aria-label="Woke at (time)" value={wokeClock} onChange={(e) => setWokeClock(e.target.value)} />
              <button
                type="button"
                className="chip on"
                disabled={!wokeClock}
                onClick={() => {
                  const [h, m] = wokeClock.split(':').map(Number);
                  const d = new Date(s.sleep.current!.at);
                  let t = new Date(d.getFullYear(), d.getMonth(), d.getDate(), h, m).getTime();
                  if (t <= s.sleep.current!.at) t += DAY;
                  wokeAt(Math.max(0, Math.round((now - Math.min(t, now)) / MINUTE)));
                }}
              >
                Set
              </button>
            </span>
          </p>
        )}
        {kind === 'accident' && (
          <Field label="Which">
            <Segmented
              value={accident}
              onChange={setAccident}
              options={[
                { value: 'wee', label: 'Wee' },
                { value: 'poo', label: 'Poo' },
              ]}
            />
          </Field>
        )}
        <Field label="Day">
          <div className="conv-filter">
            <button type="button" className={`chip${onToday ? ' on' : ''}`} aria-pressed={onToday} onClick={() => pickDay(todayISO)}>
              Today
            </button>
            <button type="button" className={`chip${day === yesterdayISO ? ' on' : ''}`} aria-pressed={day === yesterdayISO} onClick={() => pickDay(yesterdayISO)}>
              Yesterday
            </button>
            <label className={`chip earlier-clock${!onToday && day !== yesterdayISO ? ' on' : ''}`}>
              Another day
              <input type="date" aria-label="Another day" value={onToday || day === yesterdayISO ? '' : day} max={todayISO} min={toISODate(now - 60 * DAY)} onChange={(e) => pickDay(e.target.value)} />
            </label>
          </div>
        </Field>
        {/* "45 min ago" only makes sense today; other days use the exact times below. */}
        {onToday && (
          <Field label={timed ? (kind === 'nap' ? 'Went down — roughly' : 'Started — roughly') : 'When — roughly'}>
            <div className="conv-filter">
              {AGO.map((m) => (
                <button key={m} type="button" className={`chip${'ago' in when && when.ago === m ? ' on' : ''}`} aria-pressed={'ago' in when && when.ago === m} onClick={() => setWhen({ ago: m })}>
                  {agoLabel(m)}
                </button>
              ))}
            </div>
          </Field>
        )}
        {timed && (
          <Field label={kind === 'nap' ? 'Slept for — roughly' : 'Lasted — roughly'}>
            <div className="conv-filter">
              {LASTED.map((m) => (
                <button key={m} type="button" className={`chip${lasted === m ? ' on' : ''}`} aria-pressed={lasted === m} onClick={() => setLasted(m)}>
                  {lastedLabel(m)}
                </button>
              ))}
              <button type="button" className={`chip${lasted === 'ongoing' ? ' on' : ''}`} aria-pressed={lasted === 'ongoing'} onClick={() => setLasted('ongoing')}>
                {kind === 'nap' ? 'Still asleep' : 'Still going'}
              </button>
            </div>
          </Field>
        )}
        <div className="earlier-times" role="group" aria-label="Exact times">
          <TimeField
            label={kind === 'nap' ? 'Went down' : timed ? 'Started' : 'At'}
            value={at}
            onChange={(t) => setWhen({ clock: formatTime(t), date: toISODate(t) })}
            max={now}
          />
          {timed && (
            <>
              {lasted === 'ongoing' ? (
                <div className="earlier-time">
                  <span className="field-label">{kind === 'nap' ? 'Woke up' : 'Finished'}</span>
                  <button type="button" className="chip" onClick={() => setLasted({ until: formatTime(Math.min(now, at + 60 * MINUTE)) })}>
                    {kind === 'nap' ? 'Still asleep · set a time' : 'Still going · set a time'}
                  </button>
                </div>
              ) : (
                <TimeField
                  label={kind === 'nap' ? 'Woke up' : 'Finished'}
                  value={entry.end ?? lastedEnd(lasted, at) ?? now}
                  onChange={(t) => setLasted({ until: formatTime(t) })}
                  max={now}
                />
              )}
            </>
          )}
        </div>
        <p className={`callout small ${overlap || future ? 'tone-warn' : 'tone-info'}`}>
          {at > now ? (
            <>That’s later than now — for last night, pick Yesterday.</>
          ) : endTooLate ? (
            <>He can’t have woken after now — check the two times (an end before the start counts as the next day).</>
          ) : moveOpen ? (
            <>
              {label} already logged from {formatTime(moveOpen.at)} — this moves its start to <strong>{formatTime(at)}</strong>.
            </>
          ) : (
            <>
              <strong>{summary}</strong>
              {overlap && <> · overlaps one already logged at {formatTime(overlap.at)}{timed ? '' : ' — probably the same one'}</>}
              {grams ? <> · {grams} g</> : null}
            </>
          )}
        </p>
        {added.length > 0 && (
          <ul className="earlier-added" aria-label="Added">
            {added.map((a) => (
              <li key={a.id}>
                <span>{a.label}</span>
                <button
                  type="button"
                  className="link"
                  onClick={() => {
                    a.undo();
                    setAdded((list) => list.filter((x) => x.id !== a.id));
                  }}
                >
                  <Undo2 aria-hidden className="inline-icon" /> Undo
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Sheet>
  );
}
